import { useCallbackEvent } from '@dni-react-hooks/use-callback-event'
import { type AnimationEventHandler, useState } from 'react'

export interface UseIsExitedProps<Element extends HTMLElement> {
  show?: boolean
  onAnimationStart?: AnimationEventHandler<Element>
  onAnimationEnd?: AnimationEventHandler<Element>
  /**
   * Callback fired when the exit animation has finished.
   * This is useful for chaining dialogs or performing cleanup after the component has fully exited.
   */
  onExited?: () => void
}

/**
 * To improve UI experience, if is opened set exited to true after the animation end.
 */
export const useIsExited = <Element extends HTMLElement>(props: UseIsExitedProps<Element>) => {
  const { show, onAnimationStart, onAnimationEnd, onExited } = props
  const [isExited, setIsExited] = useState(!show)

  const handleAnimationStart: AnimationEventHandler<Element> = useCallbackEvent((event) => {
    onAnimationStart?.(event)
  })

  const handleAnimationEnd: AnimationEventHandler<Element> = useCallbackEvent((event) => {
    setIsExited(!show)
    onAnimationEnd?.(event)
    // Call onExited only when the exit animation has finished (show is false)
    if (!show) {
      onExited?.()
    }
  })

  return { isExited, onAnimationStart: handleAnimationStart, onAnimationEnd: handleAnimationEnd }
}
